import { useEffect, useState } from 'react';
import { posthog } from '@/utils/posthog';
import { Chapter } from '@/types';

interface UseReadingAnalyticsArgs {
  bookId?: string;
  book?: { title?: string } | null;
  visibleChapter: number;
  chapters: Chapter[];
  isChapterUnlocked: (chapter: Chapter) => boolean;
}

interface UseReadingAnalyticsResult {
  hasStartedReading: boolean;
}

export function useReadingAnalytics({
  bookId,
  book,
  visibleChapter,
  chapters,
  isChapterUnlocked,
}: UseReadingAnalyticsArgs): UseReadingAnalyticsResult {
  const [hasStartedReading, setHasStartedReading] = useState(false);

  useEffect(() => {
    const chapter = chapters.find((ch) => ch.sequence === visibleChapter);
    if (book && chapter) {
      posthog.capture('chapter_viewed', {
        book_id: bookId,
        book_title: book.title,
        chapter_id: chapter._id,
        chapter_sequence: chapter.sequence,
        chapter_title: chapter.title,
        is_unlocked: isChapterUnlocked(chapter),
        token_cost: chapter.tokenCost,
      });
    }
  }, [visibleChapter, book, chapters, bookId, isChapterUnlocked]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const heroHeight = window.innerHeight * 0.5;

      if (scrollTop > heroHeight && !hasStartedReading) {
        setHasStartedReading(true);
        posthog.capture('reading_started', {
          book_id: bookId,
          book_title: book?.title,
          chapter_sequence: visibleChapter,
          method: 'scroll',
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasStartedReading, bookId, book?.title, visibleChapter]);

  return { hasStartedReading };
}
